import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import KidLayout from '../../components/Layout/KidLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import '../../styles/brand-typography.css';
import './KidMoneyFlow.css';
import { useKidAuth } from '../../contexts/KidAuthContext';
import { dbKidData as db } from '../../services/supabase';

const KidAddChore = () => {
  const navigate = useNavigate();
  const {
    currentKid,
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useKidAuth();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;

  const [title, setTitle] = useState('');
  const [amountDollars, setAmountDollars] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toCents = (val) => {
    const cleaned = String(val ?? '').replace(/[^0-9.]/g, '');
    const num = Number(cleaned);
    if (!Number.isFinite(num)) return 0;
    return Math.round(num * 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }
    setError('');
    setSaving(true);

    try {
      if (!currentKid?.id || !currentKid?.family_id) {
        throw new Error('Kid profile not loaded yet.');
      }

      const t = title.trim();
      const cents = toCents(amountDollars);

      if (!t || t.length < 2) throw new Error('Chore title must be at least 2 characters.');
      if (cents <= 0) throw new Error('Chore value must be greater than $0.');

      // Create a PERSONAL chore owned by this kid
      await db.createPersonalChore({
        family_id: currentKid.family_id,
        title: t,
        value_cents: cents,
        is_active: true,
        created_by: 'child',
        created_by_child_id: currentKid.id,
        assigned_child_id: currentKid.id,
        source: 'child',
      });

      navigate('/kid/chores');
    } catch (err) {
      console.error('KidAddChore: error', err);
      setError(err?.message || 'Unable to create chore.');
    } finally {
      setSaving(false);
    }
  };

  if (!currentKid) {
    navigate('/kid/dashboard');
    return null;
  }

  return (
    <KidLayout className="mypiggy-brand kid-money-flow" title="Add a Chore" avatar={currentKid.avatar} showBack>
      <div className="space-y-4">
        <Card className="p-4">
          <h2 className="flex items-center gap-2 text-xl font-extrabold mb-2">
            <MyPiggyIcon name="do-chores" size="md" />
            Add My Own Chore
          </h2>
          <p className="text-base font-bold leading-relaxed text-slate-600 mb-4">
            Add a chore like helping in the yard or pet sitting. Once you finish it, press I Did It! on the Do Chores page. Your family’s approval rules apply.
          </p>

          {error ? (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
              {error}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Chore Title"
              name="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Babysat for Mrs. Smith"
              required
              autoComplete="off"
            />

            <Input
              label="How much is it worth?"
              name="amount"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              placeholder="10.00"
              required
              autoComplete="off"
            />

            <div className="grid grid-cols-2 gap-3">
              <Button type="button" variant="secondary" onClick={() => navigate('/kid/chores')}>
                Cancel
              </Button>

              <Button type="submit" variant="kid" disabled={saving || isWriteLocked}>
                {saving ? 'Saving...' : 'Save Chore'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </KidLayout>
  );
};

export default KidAddChore;

